import {
  COMPLEXITY_SEGMENT_ZONE_BY_INDEX,
  LEGACY_COMPLEXITY_STEP_SIZE,
  complexitySegmentValues,
  isValidComplexityStepSize,
  nearestComplexitySegmentIndex,
  snapToComplexitySegment,
  type ComplexitySegmentZone,
} from '@arcexperts/core-types'
import type { UsageCorrection } from '@/schemas/general/types'

export const DIMENSION_SLIDER_FIELDS = [
  'sliderCreative',
  'sliderTechnical',
  'sliderSpatial',
  'sliderOrganizational',
] as const

/** Segment values for a step size; without one (socket before core-types 1.26.0) the legacy grid. */
export function qualitySegmentValues(stepSize: number | undefined): number[] {
  return complexitySegmentValues(isValidComplexityStepSize(stepSize) ? stepSize : LEGACY_COMPLEXITY_STEP_SIZE)
}

export function qualitySegmentZone(values: readonly number[], value: number): ComplexitySegmentZone {
  return COMPLEXITY_SEGMENT_ZONE_BY_INDEX[nearestComplexitySegmentIndex(values, value)]
}

/**
 * Moves the quality adjustment factor of the usage corrections onto the segments of the
 * current step size: the slider values go to the closest segment, a VAL-156 factor override
 * becomes the overall slider, and so does a legacy final min/m² value whose base rate is known.
 * Runs when the user accepts a setup parameter update. Returns true when anything changed.
 */
export function migrateQualityFactorSegments(
  corrections: UsageCorrection[],
  values: readonly number[],
  baseMinutesForUsage?: (usageIndex: number) => number
): boolean {
  let changed = false
  const set = <K extends keyof UsageCorrection>(correction: UsageCorrection, key: K, value: UsageCorrection[K]) => {
    if (correction[key] === value) return
    correction[key] = value
    changed = true
  }

  for (const correction of corrections) {
    for (const field of DIMENSION_SLIDER_FIELDS) {
      const value = correction[field]
      if (typeof value === 'number') set(correction, field, snapToComplexitySegment(values, value))
    }

    const base = baseMinutesForUsage?.(correction.usageIndex) ?? 0
    const legacyFinalFactor = correction.manualComplexity && base > 0 ? correction.manualComplexity / base : null
    const overrideFactor = correction.complexityFactorOverride ?? legacyFinalFactor
    if (overrideFactor != null) {
      set(correction, 'complexityMode', 'OVERALL')
      set(correction, 'sliderOverall', snapToComplexitySegment(values, overrideFactor))
      set(correction, 'complexityFactorOverride', null)
      set(correction, 'manualComplexity', null)
    } else if (typeof correction.sliderOverall === 'number') {
      set(correction, 'sliderOverall', snapToComplexitySegment(values, correction.sliderOverall))
    }
  }
  return changed
}
